import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardHeader,
} from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Spinner } from "@/components/ui/spinner";
import { APP_VERSION } from "@/config/version";
import { useToast } from "@/contexts/ToastContext";
import { getPhones, selectPhoneAndSendOtp } from "@/services";
import { OtpType } from "@/types";
import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface PhoneNumber {
  phoneId: string;
  phoneNumber: string;
}

interface LocationState {
  phones: PhoneNumber[];
}

const PhoneSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;
  const { showToast } = useToast();
  const [selectedPhone, setSelectedPhone] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [phones, setPhones] = useState<PhoneNumber[]>([]);
  const [isLoadingPhones, setIsLoadingPhones] = useState(true);

  // Carregar telefones da API ao montar o componente
  useEffect(() => {
    const loadPhones = async () => {
      try {
        setIsLoadingPhones(true);
        const phonesData = await getPhones();
        setPhones(phonesData);
      } catch (error) {
        console.error('Erro ao carregar telefones:', error);
        showToast({
          title: 'Erro ao carregar telefones',
          type: "error"
        });
        // Fallback para telefones do state se houver
        if (state?.phones) {
          setPhones(state.phones);
        }
      } finally {
        setIsLoadingPhones(false);
      }
    };

    loadPhones();
  }, []); // Array vazio para executar apenas uma vez

  const handleBack = () => {
    navigate(-1);
  };

  const handleSubmit = async () => {
    if (selectedPhone) {
      setIsLoading(true);
      
      try {
        const selectedPhoneData = phones.find(p => p.phoneId === selectedPhone);
        if (selectedPhoneData) {
          const response = await selectPhoneAndSendOtp({
            phoneId: selectedPhone,
            type: OtpType.PHONE
          });

          // Se o endpoint retornar com sucesso (status 200), navegar para próxima tela
          navigate('/verification-code', { 
            state: { 
              phoneId: selectedPhone,
              phoneNumber: selectedPhoneData.phoneNumber,
              otpType: OtpType.PHONE
            } 
          });
        }
      } catch (error: any) {
        console.error('Erro ao enviar código:', error);
        showToast({
          title: 'Erro ao enviar código de verificação',
          type: "error"
        });
      } finally {
        setIsLoading(false);
      }
    }
  };


  // Tela de loading para carregar telefones
  if (isLoadingPhones) {
    return (
      <div className="min-h-screen bg-background-gradient-primary">
        <div className="flex flex-col items-center justify-center min-h-screen px-5">
          <div className="w-full max-w-lg">
            <Card className="shadow-xl border-0 backdrop-blur-sm md:px-6 py-4 md:py-6 gap-0">
              <CardHeader className="pb-0">
                 {/* Logo */}
                 <div className="mb-6">
                   <img
                     src="/icon_oi_tv.svg"
                     alt="Oi TV"
                     className="h-12 w-auto object-contain mx-auto"
                   />
                 </div>

                {/* Loading Spinner */}
                <div className="flex flex-col items-center justify-center py-8">
                  <Spinner className="h-12 w-12 text-primary mb-4" />
                  <p className="text-lg font-medium text-gray-800 text-left">
                    Carregando telefones
                  </p>
                </div>
              </CardHeader>
            </Card>
          </div>
        </div>
      </div>
    );
  }


  // Tela de loading para envio de código
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background-gradient-primary">
        <div className="flex flex-col items-center justify-center min-h-screen px-5">
          <div className="w-full max-w-lg">
            <Card className="shadow-xl border-0 backdrop-blur-sm md:px-6 py-4 md:py-6 gap-0">
              <CardHeader className="pb-0">
                 {/* Logo */}
                 <div className="mb-6">
                   <img
                     src="/icon_oi_tv.svg"
                     alt="Oi TV"
                     className="h-12 w-auto object-contain mx-auto"
                   />
                 </div>

                {/* Loading Spinner */}
                <div className="flex flex-col items-center justify-center py-8">
                  <Spinner className="h-12 w-12 text-primary mb-4" />
                  <p className="text-lg font-medium text-gray-800 text-left">
                    Enviando código
                  </p>
                </div>
              </CardHeader>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-gradient-primary">
      {/* Main Content */}
      <div className="flex flex-col items-center justify-center min-h-screen px-5">
        <div className="w-full max-w-lg">
          <Card className="shadow-xl border-0 backdrop-blur-sm md:px-6 py-4 md:py-6 gap-0">
            <CardHeader className="pb-0">
              {/* Header with Back Button and Logo */}
              <div className="mb-6 flex items-center justify-between">
                <button
                  onClick={handleBack}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <ArrowLeft className="h-6 w-6 text-gray-600" />
                </button>
                <img
                  src="/icon_oi_tv.svg"
                  alt="Oi TV"
                  className="h-12 w-auto object-contain"
                />
                <div className="w-10"></div> {/* Spacer para manter o logo centralizado */}
              </div>

              {/* Title */}
              <div className="space-y-2">
                <h1 className="text-2xl font-bold text-left">
                  Selecione o número de telefone para receber o código de verificação
                </h1>
                <p className="text-sm text-gray-600 text-left">
                  Enviaremos o código de verificação para o WhatsApp do número escolhido
                </p>
              </div>
            </CardHeader>

            <CardContent className="pt-6">
              <div className="space-y-4">
                <RadioGroup
                  value={selectedPhone}
                  onValueChange={setSelectedPhone}
                  className="space-y-3"
                >
                  {phones.map((phone) => (
                    <div
                      key={phone.phoneId}
                      className={`flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors cursor-pointer ${
                        selectedPhone === phone.phoneId 
                          ? 'border-primary' 
                          : 'border-gray-300'
                      }`}
                      onClick={() => setSelectedPhone(phone.phoneId)}
                    >
                      <div className="flex items-center gap-3">
                        <img
                          src="/whatsapp-ico.svg"
                          alt="WhatsApp"
                          className="h-5 w-5"
                        />
                        <span className="text-gray-800 font-medium">
                          {phone.phoneNumber}
                        </span>
                      </div>
                      <RadioGroupItem 
                        value={phone.phoneId} 
                        isSelected={selectedPhone === phone.phoneId}
                      />
                    </div>
                  ))}
                </RadioGroup>

                <div className="pt-4">
                  <Button
                    onClick={handleSubmit}
                    className="w-full bg-primary hover:bg-green-600"
                    disabled={!selectedPhone}
                  >
                    Enviar código de verificação
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Footer Info */}
          <div className="flex items-center justify-start mt-6 gap-2">
            <img
              src="/important_icon.svg"
              alt="Informação importante"
              className="w-5 h-5"
            />
            <p className="text-xs text-white text-left">
              Entre em contato com o atendimento através do 10631 caso o seu WhatsApp não apareça na relação acima
            </p>
          </div>

          <div className="flex justify-center items-center mt-4">
            <span className="text-xs text-white">{APP_VERSION}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhoneSelection;
